import { ActionIcon, Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Asterisk } from '@/components/Common/Asterisk';

interface TagGroup {
  id: string;
  name: string;
  tags: string[];
  editTime: string;
}

interface TagEditModalProps {
  opened: boolean;
  onClose: () => void;
  editingItem: TagGroup | null;
  isCreateMode: boolean;
  updateLoading: boolean;
  onSave: (currentTagInput: string) => void;
  onEditingItemChange: (item: TagGroup) => void;
  existingTags: string[];
  existingGroupNames: string[];
}

const TagEditModal = ({
  opened,
  onClose,
  editingItem,
  isCreateMode,
  updateLoading,
  onSave,
  onEditingItemChange,
  existingTags,
  existingGroupNames,
}: TagEditModalProps) => {
  const [currentTagInput, setCurrentTagInput] = useState('');
  const { t } = useTranslation();

  const addTags = (tagText: string) => {
    if (!editingItem) return;

    const MAX_TAG_LENGTH = 20;

    const newTags = tagText
      .split(/[、,，]/)
      .map((item) => item.trim())
      .filter(Boolean);

    const validTags: string[] = [];
    const duplicateTags: string[] = [];
    const tooLongTags: string[] = [];

    newTags.forEach((tag) => {
      // 校验单个标签长度
      if (tag.length > MAX_TAG_LENGTH) {
        tooLongTags.push(tag);
      } else if (
        editingItem.tags.some((t) => t.toLowerCase() === tag.toLowerCase()) ||
        existingTags.some((t) => t.toLowerCase() === tag.toLowerCase())
      ) {
        duplicateTags.push(tag);
      } else {
        validTags.push(tag);
      }
    });

    if (tooLongTags.length) {
      notifications.show({
        message: t('tag.tag_too_long_message', { tags: tooLongTags.join('、'), max: MAX_TAG_LENGTH }),
        color: 'yellow',
      });
    }

    if (duplicateTags.length) {
      notifications.show({
        message: t('tag.duplicate_tag_across_groups_message', { tag: duplicateTags.join('、') }),
        color: 'yellow',
      });
    }

    onEditingItemChange({
      ...editingItem,
      tags: [...editingItem.tags, ...validTags],
    });
  };

  const removeTag = (indexToRemove: number) => {
    if (!editingItem) return;

    onEditingItemChange({
      ...editingItem,
      tags: editingItem.tags.filter((_, index) => index !== indexToRemove),
    });
  };

  const handleGroupNameChange = (newName: string) => {
    if (!editingItem) return;

    onEditingItemChange({
      ...editingItem,
      // 保留原始输入（含中间空格），首尾空白在保存时统一 trim
      name: newName,
    });
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const LIMIT = 10;

      if ((editingItem?.tags?.length || 0) >= LIMIT) {
        notifications.show({
          title: t('tag.length_limit_title'),
          message: t('tag.length_limit_message', { limit: LIMIT }),
          color: 'yellow',
        });
        return;
      }
      addTags(currentTagInput.trim());
      setCurrentTagInput('');
    }
  };

  const handleSave = () => {
    const normalizedNewName = editingItem?.name.toLowerCase().trim();

    if (normalizedNewName && editingItem?.name.trim() && existingGroupNames.includes(normalizedNewName)) {
      notifications.show({
        message: t('tag.duplicate_group_name_message', { groupName: editingItem.name.trim() }),
        color: 'yellow',
      });
      return;
    }

    onSave(currentTagInput.trim());
    // 每次都清空输入框
    setCurrentTagInput('');
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size="md"
      title={isCreateMode ? t('tag.create_group') : t('tag.edit_group')}
    >
      <Stack>
        <TextInput
          label={
            <p>
              {t('tag.group_name')}
              <Asterisk />
            </p>
          }
          value={editingItem?.name || ''}
          maxLength={20}
          onChange={(e) => handleGroupNameChange(e.target.value)}
        />
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            {t('tag.tags')}
            <Asterisk />
          </label>
          <TextInput
            placeholder={t('tag.input_tags')}
            value={currentTagInput}
            onChange={(e) => setCurrentTagInput(e.target.value)}
            onKeyDown={handleTagInputKeyDown}
          />

          {editingItem && editingItem.tags.length > 0 && (
            <div className="mt-3">
              <Group gap="xs">
                {editingItem.tags.map((tag, index) => (
                  <Button
                    key={index}
                    variant="filled"
                    size="xs"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        onClick={() => removeTag(index)}
                        className="text-white hover:bg-white/20"
                      >
                        ×
                      </ActionIcon>
                    }
                    className="pr-1"
                  >
                    {tag}
                  </Button>
                ))}
              </Group>
            </div>
          )}
        </div>

        <Group mt={30} justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t('tag.cancel')}
          </Button>
          <Button loading={updateLoading} onClick={handleSave} color="violet">
            {t('tag.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default TagEditModal;
